import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Mail, Github, Twitter, Instagram, Linkedin, Youtube, Globe, MapPin, Activity } from 'lucide-react';
import './App.css';

function App() {
  const [time, setTime] = useState(new Date());
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -100, y: -100 });
  const nodesRef = useRef([]);
  const mouseColorRef = useRef('#22d3ee');
  const lastMouseMoveRef = useRef(0);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const mouseTravelRef = useRef(0);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const colorChangeThreshold = 300;
  const [avatarBorderColor, setAvatarBorderColor] = useState('#ec4899');
  const currentColorIndexRef = useRef(1);
  const colorTransitionRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const interpolateColor = (color1, color2, factor) => {
    const c1 = {
      r: parseInt(color1.slice(1, 3), 16),
      g: parseInt(color1.slice(3, 5), 16),
      b: parseInt(color1.slice(5, 7), 16)
    };
    const c2 = {
      r: parseInt(color2.slice(1, 3), 16),
      g: parseInt(color2.slice(3, 5), 16),
      b: parseInt(color2.slice(5, 7), 16)
    };
    
    const r = Math.round(c1.r + (c2.r - c1.r) * factor);
    const g = Math.round(c1.g + (c2.g - c1.g) * factor);
    const b = Math.round(c1.b + (c2.b - c1.b) * factor);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Node {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = 3.5;
        this.glowIntensity = 0;
        this.glowDecay = 0.02;
        this.color = '#6b7280';
        this.glowColor = '#6b7280';
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        this.x = Math.max(0, Math.min(canvas.width, this.x));
        this.y = Math.max(0, Math.min(canvas.height, this.y));

        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          this.glowIntensity = Math.min(1, 1 - distance / 150);
          this.glowColor = mouseColorRef.current;
        } else {
          this.glowIntensity = Math.max(0, this.glowIntensity - this.glowDecay);
        }
      }

      draw() {
        if (this.glowIntensity > 0) {
          ctx.shadowBlur = 25 * this.glowIntensity;
          ctx.shadowColor = this.glowColor;
          ctx.fillStyle = this.glowColor;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = this.color;
        }

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const nodeCount = 80;
    nodesRef.current = Array.from({ length: nodeCount }, () => new Node());

    const handleMouseMove = (e) => {
      const dx = e.clientX - lastMousePosRef.current.x;
      const dy = e.clientY - lastMousePosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      mouseTravelRef.current += distance;
      
      colorTransitionRef.current = Math.min(1, mouseTravelRef.current / colorChangeThreshold);
      
      const colors = ['#22d3ee', '#ec4899', '#a855f7'];
      const currentColorIndex = currentColorIndexRef.current;
      const nextColorIndex = (currentColorIndex + 1) % colors.length;
      
      const interpolatedColor = interpolateColor(
        colors[currentColorIndex],
        colors[nextColorIndex],
        colorTransitionRef.current
      );
      
      mouseColorRef.current = interpolatedColor;
      setAvatarBorderColor(interpolatedColor);
      
      if (mouseTravelRef.current >= colorChangeThreshold) {
        currentColorIndexRef.current = nextColorIndex;
        mouseTravelRef.current = 0;
        colorTransitionRef.current = 0;
      }
      
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      mouseRef.current = { x: e.clientX, y: e.clientY };
      setCursorPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.fillStyle = '#0a0a0c';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      nodesRef.current.forEach(node => {
        node.update();
        node.draw();
      });

      ctx.shadowBlur = 0;
      nodesRef.current.forEach((node, i) => {
        nodesRef.current.slice(i + 1).forEach(otherNode => {
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.3;
            const avgGlow = (node.glowIntensity + otherNode.glowIntensity) / 2;

            if (avgGlow > 0) {
              const glowColor = node.glowIntensity > otherNode.glowIntensity ? node.glowColor : otherNode.glowColor;
              ctx.strokeStyle = glowColor + Math.floor(opacity * avgGlow * 255).toString(16).padStart(2, '0');
              ctx.lineWidth = 1 + avgGlow * 1.5;
            } else {
              ctx.strokeStyle = `rgba(107, 114, 128, ${opacity})`;
              ctx.lineWidth = 1;
            }

            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.stroke();
          }
        });
      });

      const nearestNodes = nodesRef.current
        .map(node => ({
          node,
          distance: Math.sqrt(
            Math.pow(mouseRef.current.x - node.x, 2) + 
            Math.pow(mouseRef.current.y - node.y, 2)
          )
        }))
        .filter(item => item.distance < 200)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 3);

      nearestNodes.forEach(({ node, distance }) => {
        const opacity = (1 - distance / 200) * 0.6;
        ctx.strokeStyle = mouseColorRef.current + Math.floor(opacity * 255).toString(16).padStart(2, '0');
        ctx.lineWidth = 2;
        ctx.shadowBlur = 10;
        ctx.shadowColor = mouseColorRef.current;

        ctx.beginPath();
        ctx.moveTo(mouseRef.current.x, mouseRef.current.y);
        ctx.lineTo(node.x, node.y);
        ctx.stroke();
      });

      ctx.shadowBlur = 0;

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const formatTime = date => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const links = [
    {
      title: "Vintio Interactive",
      subtitle: "Visit our studio",
      url: "https://vintiointeractive.com",
      icon: Globe,
      color: "text-cyan-400",
      borderColor: "hover:border-cyan-400",
      bgGradient: "from-cyan-500/20 to-transparent"
    },
    {
      title: "GitHub",
      subtitle: "Open source projects",
      url: "https://github.com/VittorioCodes",
      icon: Github,
      color: "text-purple-400",
      borderColor: "hover:border-purple-400",
      bgGradient: "from-purple-500/20 to-transparent"
    },
    {
      title: "Twitter / X",
      subtitle: "@VittorioKun",
      url: "https://x.com/VittorioKun",
      icon: Twitter,
      color: "text-cyan-300",
      borderColor: "hover:border-cyan-300",
      bgGradient: "from-cyan-400/20 to-transparent"
    },
    {
      title: "Instagram",
      subtitle: "@sayvittorio",
      url: "https://instagram.com/sayvittorio",
      icon: Instagram,
      color: "text-pink-400",
      borderColor: "hover:border-pink-400",
      bgGradient: "from-pink-400/20 to-transparent"
    },
    {
      title: "Contact",
      subtitle: "vito@vintiointeractive.info",
      url: "mailto:vito@vintiointeractive.info",
      icon: Mail,
      color: "text-amber-400",
      borderColor: "hover:border-amber-400",
      bgGradient: "from-amber-500/20 to-transparent"
    }
  ];

  return (
    <div className="app-container">
      <canvas ref={canvasRef} className="background-canvas" />

      <div 
        className="custom-cursor"
        style={{
          left: `${cursorPos.x}px`,
          top: `${cursorPos.y}px`,
        }}
      >
        <motion.div
          className="cursor-dot"
          style={{
            backgroundColor: mouseColorRef.current,
            boxShadow: `0 0 30px ${mouseColorRef.current}, 0 0 50px ${mouseColorRef.current}`
          }}
          animate={{
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="vhs-overlay-left">
        <Activity className="activity-icon" />
        <span>REC</span>
        <span className="time-display">{formatTime(time)}</span>
      </div>
      
      <div className="vhs-overlay-right">
        <span>LINK-MODE</span>
      </div>

      <div className="main-content">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="profile-section"
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="avatar"
            style={{
              border: `4px solid ${avatarBorderColor}`,
              boxShadow: `0 0 20px ${avatarBorderColor}`
            }}
          >
            <div className="avatar-gradient" />
            <div className="avatar-letter">
              <span>V</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="profile-name"
          >
            VITO
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="profile-subtitle"
          >
            Indie Developer / Designer for some stuff, hopefully...
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="profile-location"
          >
            <MapPin className="location-icon" />
            <span>Somewhere far away</span>
          </motion.div>

          <div className="profile-divider" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="links-container"
        >
          {links.map((link, index) => (
            <motion.a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.9 + index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02, x: 5 }}
              whileTap={{ scale: 0.98 }}
              className={`link-card ${link.borderColor}`}
            >
              <div className={`link-gradient ${link.bgGradient}`} />
              
              <div className="link-content">
                <link.icon className={`link-icon ${link.color}`} />
                <div className="link-text">
                  <h3>{link.title}</h3>
                  <p className={link.color}>{link.subtitle}</p>
                </div>
                <ExternalLink className="external-icon" />
              </div>

              <div className="corner-tl" />
              <div className="corner-br" />
            </motion.a>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          className="social-icons"
        >
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.6 }}
          className="footer-text"
        >
          <p>© 2025 Vintio Interactive • vintiointeractive.info</p>
        </motion.div>
      </div>

      <div className="decorative-grid" />
    </div>
  );
}

export default App;